/**
 * Authentication service for client-side API calls
 */
import apiService from './apiService';
import fallbackService from './fallbackService';
import { clearAuth } from '../utils/auth';

// Flag to control whether to use fallback service
const USE_FALLBACK = false;

const authService = {
  /**
   * Login user and get token
   * @param {string} username - Username
   * @param {string} password - Password
   * @returns {Promise<Object>} - User data and token
   */
  async login(username, password) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback authentication service');
        return fallbackService.auth.login(username, password);
      }
      
      return await apiService.post('/auth/login', { username, password });
    } catch (error) {
      console.error('Login API error:', error);
      // Do not fall back to mock authentication
      throw error;
    }
  },

  /**
   * Verify token and get user data
   * @returns {Promise<Object>} - User data
   */
  async verifyToken() {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback token verification');
        return fallbackService.auth.verifyToken();
      }
      
      const resp = await apiService.get('/auth/verify');
      return resp.user;
    } catch (error) {
      console.error('Token verification error:', error);
      // Do not fall back to mock verification
      throw error;
    }
  },

  /**
   * Get user permissions
   * @returns {Promise<Object>} - User permissions
   */
  async getPermissions() {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback permissions service');
        return fallbackService.auth.getPermissions();
      }
      
      return await apiService.get('/auth/permissions');
    } catch (error) {
      console.error('Permissions error:', error);
      // Do not fall back to mock permissions
      throw error;
    }
  },

  /**
   * Get token from localStorage
   * @returns {string|null} - JWT token
   */
  getToken() {
    return localStorage.getItem('token');
  },

  /**
   * Set token in localStorage
   * @param {string} token - JWT token
   */
  setToken(token) {
    localStorage.setItem('token', token);
  },

  /**
   * Remove token from localStorage
   */
  removeToken() {
    localStorage.removeItem('token');
  },

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>} - Response data
   */
  async requestPasswordReset(email) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback password reset request');
        return { success: true, message: 'Password reset email sent' };
      }
      
      return await apiService.post('/auth/request-reset', { email });
    } catch (error) {
      console.error('Password reset request error:', error);
      throw error;
    }
  },

  /**
   * Verify a password reset token
   * @param {string} token - Reset token
   * @returns {Promise<Object>} - Response data
   */
  async verifyResetToken(token) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback reset token verification');
        return { valid: true };
      }

      return await apiService.get('/auth/verify-reset-token', { params: { token } });
    } catch (error) {
      console.error('Reset token verification error:', error);
      throw error;
    }
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Response data
   */
  async resetPassword(token, newPassword) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback password reset');
        return { success: true, message: 'Password reset successful' };
      }
      
      return await apiService.post('/auth/reset-password', { token, newPassword });
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  },

  /**
   * Change password for logged in user
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Response data
   */
  async changePassword(currentPassword, newPassword) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback password change');
        return { success: true, message: 'Password changed successfully' };
      }
      
      return await apiService.post('/auth/change-password', { currentPassword, newPassword });
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  }
};

export default authService;
